// API base URL is handled by createApiUrl in api.utils
import authService from './auth.service';
import { AuthenticationError, NetworkError, ApiResponseError } from './errors';
import { createApiUrl, fetchWithRetry } from './api.utils';
import { recordApiTiming, newRequestId } from './perfTelemetry';
import { markSelfMutation, isSelfFlashableMutationUrl } from '../selfMutationSignal';

/**
 * Turns a failed refresh into the right error.
 *
 * refreshAccessToken() returns null both when the session is over and when the
 * request never reached the server. Only the first may travel on as an
 * AuthenticationError: that is what dispatches auth:sessionExpired, and the
 * handler answers it with POST /stoc/auth/v2/logout — which deletes the 30-day
 * refresh cookie for good. On a phone the second case is routine (Chrome
 * freezes a backgrounded tab and its in-flight fetches die), so reporting it as
 * a session expiry logged people out every time they left the app and came
 * back. A transient failure stays a NetworkError and leaves the cookie alone.
 */
function refreshFailureError() {
  if (authService.getLastRefreshFailure() === 'auth') {
    return new AuthenticationError('Session expired');
  }
  return new NetworkError('Nu s-a putut reînnoi sesiunea (conexiune întreruptă). Reîncearcă peste câteva secunde.');
}

/**
 * ApiClient class that handles API requests with token refresh capability
 */
class ApiClient {
  constructor() {
    // On page load in any tab, try a silent refresh once to bootstrap access token from HttpOnly cookie
    if (typeof window !== 'undefined') {
      try {
        // Avoid spamming: only run once per page load
        authService.refreshAccessToken().catch(() => {});
      } catch { /* noop */ }
    }
  }
  /**
   * Makes an API request with automatic token refresh handling
   * @param {string} url - The URL to fetch
   * @param {object} options - Fetch options
   * @returns {Promise<Response>} The fetch response
   */
  async request(url, options = {}) {
    // Setup default headers
    options.headers = options.headers || {};

    // Performance attribution: the id + start time travel with the request;
    // the backend echoes the id and returns Server-Timing, so every slow call
    // decomposes into network vs server vs DB (see perfTelemetry.js).
    const perfRid = newRequestId();
    const perfT0 = performance.now();
    options.headers['X-Request-ID'] = perfRid;

    // Add idempotency key for POST, PUT, and PATCH requests to prevent duplicate submissions
    if (['POST', 'PUT', 'PATCH'].includes(options.method)) {
      options.headers['Idempotency-Key'] = crypto.randomUUID();
    }
    
    // Add auth header if we have a token
    if (authService.isAuthenticated()) {
      const token = authService.getAccessToken();
      if (token) {
        options.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    try {
      // Check if token is expired before making the request
      if (authService.isAuthenticated() && authService.isTokenExpired()) {
        console.info('Token is expired, attempting to refresh...');
        const newToken = await authService.refreshAccessToken();
        
        if (newToken) {
          // Update authorization header with new token
          options.headers['Authorization'] = `Bearer ${newToken}`;
          console.info('Token refreshed successfully');
        } else {
          // If refresh failed, throw an auth error only if the session is
          // really over; a dropped connection must not end it.
          console.error('Token refresh failed');
          throw refreshFailureError();
        }
      }
      
      // Mark a self-originated CRM-record mutation BEFORE awaiting, so THIS tab
      // won't yellow-flash the echo of its own edit even if the request later
      // times out after the backend already committed (long-chain reception/
      // composite/swap endpoints run with multi-minute timeouts). Covers both
      // mutating verbs and GET-shaped mutations that opt in via marksSelfMutation
      // (the sale-document actions that lazily CREATE an AWB/invoice/label on a
      // first GET). A request that changed nothing emits no echo, so an early
      // mark is harmless. See selfMutationSignal.js.
      const _isMutatingVerb = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method);
      if ((_isMutatingVerb || options.marksSelfMutation === true) && isSelfFlashableMutationUrl(url)) {
        markSelfMutation();
      }

      // Make the request
      try {
        // Allow per-request overrides for retries and timeout
        const { maxRetries, timeoutMs, disable401Retry, ...restOptions } = options || {};
        const retries = Math.max(1, (typeof maxRetries === 'number' ? maxRetries : 1));
        const timeout = typeof timeoutMs === 'number' ? timeoutMs : 30000;
        const response = await fetchWithRetry(url, restOptions, retries, timeout);
        const finalResponse = await this._handleResponse(response, url, options);
        recordApiTiming(url, options.method || 'GET', perfRid, perfT0, finalResponse, null);
        return finalResponse;
      } catch (error) {
        const errName = (error && error.name) ? error.name : '';
        if (errName === 'AbortError') {
          recordApiTiming(url, options.method || 'GET', perfRid, perfT0, null, 'timeout');
          throw new NetworkError('Cererea a expirat (timeout) — serverul răspunde greu. Reîncearcă.');
        }
        recordApiTiming(url, options.method || 'GET', perfRid, perfT0, null, errName || 'network');
        // A fetch-level failure (server unreachable, connection reset/CORS, or a
        // proxy timeout that severs the connection mid-request) surfaces as a
        // TypeError "Failed to fetch". Replace the raw browser text with a clear
        // message so operators see "server unreachable" instead of "Failed to fetch".
        const rawMsg = (error && error.message) || '';
        if (errName === 'TypeError' || /failed to fetch|networkerror|load failed/i.test(rawMsg)) {
          throw new NetworkError('Nu s-a putut contacta serverul (conexiune întreruptă sau răspuns prea lent). Reîncearcă peste câteva secunde.');
        }
        // Normalize non-Error throws
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(String(error));
      }
    } catch (error) {
      if (error instanceof AuthenticationError) {
        // Notify application about session expiration
        window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
      }
      throw error;
    }
  }
  
  /**
   * Handles the API response, including token refresh on 401
   * @param {Response} response - The fetch response
   * @param {string} url - Original request URL
   * @param {object} options - Original request options
   * @returns {Promise<Response>} The response or retry response
   */
  async _handleResponse(response, url, options) {
    // If we get a 401 Unauthorized, try to refresh the token
    if (response.status === 401 && !options.disable401Retry) {
      console.info('Received 401 response, attempting to refresh token...');

      try {
        const newToken = await authService.refreshAccessToken();

        if (newToken) {
          // Retry the request with the new token
          console.info('Token refreshed, retrying original request');
          const { maxRetries, timeoutMs, disable401Retry, ...fetchOptions } = options;
          fetchOptions.headers['Authorization'] = `Bearer ${newToken}`;
          const retries = Math.max(1, (typeof maxRetries === 'number' ? maxRetries : 1));
          const timeout = typeof timeoutMs === 'number' ? timeoutMs : 30000;
          return fetchWithRetry(url, fetchOptions, retries, timeout);
        } else {
          console.error('Token refresh failed after 401');
          throw refreshFailureError();
        }
      } catch (error) {
        console.error('Error during token refresh after 401:', error);
        if (error instanceof AuthenticationError || error instanceof NetworkError) {
          throw error;
        }
        // The refresh itself worked and the retried request is what failed —
        // classify by the refresh, so a broken connection stays a network error.
        throw refreshFailureError();
      }
    }

    return response;
  }
  
  /**
   * Parse JSON response and handle error responses
   * @param {Response} response - Fetch response
   * @returns {Promise<any>} Parsed JSON data
   */
  async parseResponse(response) {
    // 204 No Content – return null immediately
    if (response.status === 204 || response.status === 205) {
      return null;
    }

    // Some endpoints might return an empty body with 200 OK
    const cloned = response.clone();
    const text = await cloned.text();
    if (text.trim().length === 0) {
      return null;
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Failed to parse response as JSON');
    }

    if (!response.ok) {
      // Prefer 'detail' field from API errors (e.g., FastAPI) over generic 'message'
      const detailMsg = data?.detail || data?.message;
      const errorMsg = detailMsg || `HTTP error ${response.status}`;
      // Include any error details if available
      const errorDetails = data?.details ?? (data?.detail ? { detail: data.detail } : undefined);
      throw new ApiResponseError(errorMsg, response.status, errorDetails);
    }

    return data;
  }
  
  /**
   * Performs a GET request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async get(endpoint, options = {}) {
    let url = createApiUrl(endpoint);
    
    // Handle query parameters if params option is provided
    if (options.params && typeof options.params === 'object') {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }
    
    // Remove params from options before passing to request
    const fetchOptions = { ...options };
    if ('params' in fetchOptions) {
      delete fetchOptions.params;
    }
    
    const response = await this.request(url, {
      method: 'GET',
      ...fetchOptions
    });

    if (fetchOptions.responseType === 'blob') {
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch { /* noop */ }
        let errorData;
        try {
          if (errorText) {
            errorData = JSON.parse(errorText);
          }
        } catch { /* ignore JSON parse errors */ }
        const detailMsg = errorData?.detail || errorData?.message || errorText || `HTTP error ${response.status}`;
        const errorDetails = errorData?.details ?? (errorData?.detail ? { detail: errorData.detail } : undefined);
        throw new ApiResponseError(detailMsg, response.status, errorDetails);
      }
      return response.blob();
    }
    
    return this.parseResponse(response);
  }
  
  /**
   * Performs a POST request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async post(endpoint, data, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
    if (options.responseType === 'blob') {
      if (!response.ok) await this.parseResponse(response);
      return response.blob();
    }
    return this.parseResponse(response);
  }
  
  /**
   * Performs a PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async put(endpoint, data, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
    return this.parseResponse(response);
  }
  
  /**
   * Performs a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async patch(endpoint, data, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      ...options
    });
    return this.parseResponse(response);
  }
  
  /**
   * Performs a DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async delete(endpoint, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'DELETE',
      ...options
    });
    return this.parseResponse(response);
  }
  
  /**
   * Performs a form data POST request (for file uploads etc.)
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data to send
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async postFormData(endpoint, formData, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'POST',
      body: formData,
      ...options
    });
    if (options.responseType === 'blob') {
      return response.blob();
    }
    return this.parseResponse(response);
  }
  
  /**
   * Performs a form data PUT request (for file uploads etc.)
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data to send
   * @param {object} options - Additional fetch options
   * @returns {Promise<any>} The parsed response data
   */
  async putFormData(endpoint, formData, options = {}) {
    const url = createApiUrl(endpoint);
    const response = await this.request(url, {
      method: 'PUT',
      body: formData,
      ...options
    });
    return this.parseResponse(response);
  }
}

export default new ApiClient();
